import { PropTypes } from 'prop-types';
import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, withRouter } from 'react-router-dom';
import styled from 'styled-components';

import { PageContainer } from '@ge/components/containers';
import { Loader } from '@ge/components/loader';
// import { MapView } from '@ge/components/map-view';
import ClearColumnFilter from '@ge/components/table/filters/clear-column-filter';
import { SortDirection } from '@ge/components/table/models/sort-direction';
import { useColumnState } from '@ge/components/table/use-column-state';
import { useManageColumnVisibility } from '@ge/components/table/use-manage-column-visibility';
import { useSitesTable } from '@ge/feat-monitor/data-hooks';
import { useFeaturePrefs } from '@ge/hooks/feature-prefs';
import { MonitorDefs, PresentationTypes, EntityType, StorageSiteKeys } from '@ge/models/constants';
import { ColumnSelectMenu } from '@ge/shared/components/column-select-menu';
import { DetailsDismissInert } from '@ge/shared/components/entity-details/dismiss-inert';
import { EntityDetailsContext } from '@ge/shared/context/entity-details-context';
import { useAuth, useIsMobile } from '@ge/shared/data-hooks';
import { useFilterDefs } from '@ge/shared/hooks';
import { AppScopes } from '@ge/shared/models/scopes';
import { FourOhFour } from '@ge/web-client/src/app/components/404/404';
// TODO (astone): This is an unholy reference to DWF from a feature! Must remove!
import SubNavigation from '@ge/web-client/src/app/components/navigation/sub-navigation';
// TODO (astone): This is an unholy reference to DWF from a feature! Must remove!
import PresentationToggle from '@ge/web-client/src/app/components/presentation-toggle/presentation-toggle';

import SitesTable from '../components/tables/sites-table';
import SolarSitesTable from '../components/tables/solar-sites-table';
import StorageSitesTable from '../components/tables/storage-sites-table';
import {
  defaultSiteCols,
  defaultStorageSiteCols,
  defaultSolarSiteCols,
  SitesColumnDefs,
  StorageSitesColumnDefs,
  SolarSitesColumnDefs,
  SitesNavItems,
} from '../models';

const SORT_WIND_STATE_ID = 'monitor-wind-sites';
const SORT_STORAGE_STATE_ID = 'monitor-storage-sites';
const SORT_SOLAR_STATE_ID = 'monitor-solar-sites';
const SITES = MonitorDefs.SITES;

const Load = styled.div`
  padding-top: 400px;
`;

const VALID_ENDPOINTS = ['wind', 'storage', 'solar'];

/**
 * Available sort metrics for Sites.
 */
export const SiteMetric = {
  NAME: 'site.name',
};

const stateIdMap = {
  [EntityType.WIND_SITE]: SORT_WIND_STATE_ID,
  [EntityType.STORAGE_SITE]: SORT_STORAGE_STATE_ID,
  [EntityType.SOLAR_SITE]: SORT_SOLAR_STATE_ID,
};

const columnDefsMap = {
  [EntityType.WIND_SITE]: SitesColumnDefs,
  [EntityType.STORAGE_SITE]: StorageSitesColumnDefs,
  [EntityType.SOLAR_SITE]: SolarSitesColumnDefs,
};

const STORAGE_KEYS = {
  [EntityType.WIND_SITE]: StorageSiteKeys.MONITOR_WIND_SITE_COLUMNS,
  [EntityType.STORAGE_SITE]: StorageSiteKeys.MONITOR_STORAGE_SITE_COLUMNS,
  [EntityType.SOLAR_SITE]: StorageSiteKeys.MONITOR_SOLAR_SITE_COLUMNS,
};

/**
 * Set list of view tyoes for Presentation Toggle
 */
const PresentationViews = [PresentationTypes.LIST];

const MonitorSites = (props) => {
  const { t, ready } = useTranslation(['monitor.sites'], {
    useSuspense: false,
  });

  const isMobile = useIsMobile();
  const { isAuthorized } = useAuth();
  const {
    showSiteDetails,
    showStorageSiteDetails,
    showSolarSiteDetails,
    showHybridSiteDetails,
    panelShown,
  } = useContext(EntityDetailsContext);

  const {
    location: { search },
  } = props;

  const [isActive, setActiveState] = useState(PresentationTypes.LIST);
  const [currentSearch, setCurrentSearch] = useState({});

  // One custom hook call for Wind Site Filters
  const {
    filterDefs: windSitesFilterDefs,
    filterSequence: windSitesFilterSequence,
    onChange: setWindSitesFilterDefs,
    onReset: setWindSitesReset,
  } = useFilterDefs({
    columnDefs: SitesColumnDefs,
    stateId: SORT_WIND_STATE_ID,
  });

  // One more custom hook call for Storage Site Filters
  const {
    filterDefs: storageSitesFilterDefs,
    filterSequence: storageSitesFilterSequence,
    onChange: setStorageSitesFilterDefs,
    onReset: setStorageSitesReset,
  } = useFilterDefs({
    columnDefs: StorageSitesColumnDefs,
    stateId: SORT_STORAGE_STATE_ID,
  });

  // One more custom hook call for Solar Site Filters
  const {
    filterDefs: solarSitesFilterDefs,
    filterSequence: solarSitesFilterSequence,
    onChange: setSolarSitesFilterDefs,
    onReset: setSolarSitesReset,
  } = useFilterDefs({
    columnDefs: SolarSitesColumnDefs,
    stateId: SORT_SOLAR_STATE_ID,
  });

  const [currentRow, setCurrentRow] = useState();

  useEffect(() => {
    !panelShown && setCurrentRow([]);
  }, [panelShown]);

  const containerRef = useRef(null);

  const { savePrefs, featPrefs } = useFeaturePrefs(AppScopes.MONITOR_SITES);

  const { filter } = useParams();

  // Table state management
  const {
    updateColumnVisibility: updateColumnVisibilityWind,
    setVisibleColumns: setVisibleColumnWind,
    visibleCols: visibleColsWind,
    sortDirection: sortDirectionWind,
    sortMetric: sortMetricWind,
    updateSortMetric: updateSortMetricWind,
  } = useColumnState({
    columnDefs: columnDefsMap[EntityType.WIND_SITE],
    defaultCols: defaultSiteCols,
    defaultSortMetric: SiteMetric.NAME,
    defaultSortDirection: SortDirection.ASC,
    sortStateId: stateIdMap[EntityType.WIND_SITE],
  });

  const {
    updateColumnVisibility: updateColumnVisibilityStorage,
    setVisibleColumns: setVisibleColumnStorage,
    visibleCols: visibleColsStorage,
    sortDirection: sortDirectionStorage,
    sortMetric: sortMetricStorage,
    updateSortMetric: updateSortMetricStorage,
  } = useColumnState({
    columnDefs: columnDefsMap[EntityType.STORAGE_SITE],
    defaultCols: defaultStorageSiteCols,
    defaultSortMetric: SiteMetric.NAME,
    defaultSortDirection: SortDirection.ASC,
    sortStateId: stateIdMap[EntityType.STORAGE_SITE],
  });

  const {
    updateColumnVisibility: updateColumnVisibilitySolar,
    setVisibleColumns: setVisibleColumnSolar,
    visibleCols: visibleColsSolar,
    sortDirection: sortDirectionSolar,
    sortMetric: sortMetricSolar,
    updateSortMetric: updateSortMetricSolar,
  } = useColumnState({
    columnDefs: columnDefsMap[EntityType.SOLAR_SITE],
    defaultCols: defaultSolarSiteCols,
    defaultSortMetric: SiteMetric.NAME,
    defaultSortDirection: SortDirection.ASC,
    sortStateId: stateIdMap[EntityType.SOLAR_SITE],
  });

  const UpdateColAndSort = {
    [EntityType.SOLAR_SITE]: {
      updateColumnVisibility: updateColumnVisibilitySolar,
      setVisibleColumns: setVisibleColumnSolar,
      visibleCols: visibleColsSolar,
      sortDirection: sortDirectionSolar,
      sortMetric: sortMetricSolar,
      updateSortMetric: updateSortMetricSolar,
    },
    [EntityType.STORAGE_SITE]: {
      updateColumnVisibility: updateColumnVisibilityStorage,
      setVisibleColumns: setVisibleColumnStorage,
      visibleCols: visibleColsStorage,
      sortDirection: sortDirectionStorage,
      sortMetric: sortMetricStorage,
      updateSortMetric: updateSortMetricStorage,
    },
    [EntityType.WIND_SITE]: {
      updateColumnVisibility: updateColumnVisibilityWind,
      setVisibleColumns: setVisibleColumnWind,
      visibleCols: visibleColsWind,
      sortDirection: sortDirectionWind,
      sortMetric: sortMetricWind,
      updateSortMetric: updateSortMetricWind,
    },
  };

  const FilterDefsFromSiteTypeFilter = {
    [EntityType.WIND_SITE]: windSitesFilterDefs,
    [EntityType.STORAGE_SITE]: storageSitesFilterDefs,
    [EntityType.SOLAR_SITE]: solarSitesFilterDefs,
  };

  const FilterDefsFilterSequence = {
    [EntityType.WIND_SITE]: windSitesFilterSequence,
    [EntityType.STORAGE_SITE]: storageSitesFilterSequence,
    [EntityType.SOLAR_SITE]: solarSitesFilterSequence,
  };

  const SetFilterDefsFromSiteTypeFilter = {
    [EntityType.WIND_SITE]: setWindSitesFilterDefs,
    [EntityType.STORAGE_SITE]: setStorageSitesFilterDefs,
    [EntityType.SOLAR_SITE]: setSolarSitesFilterDefs,
  };

  const resetFilters = useCallback(() => {
    const ResetFromSiteTypeFilter = {
      [EntityType.WIND_SITE]: setWindSitesReset,
      [EntityType.STORAGE_SITE]: setStorageSitesReset,
      [EntityType.SOLAR_SITE]: setSolarSitesReset,
    };
    ResetFromSiteTypeFilter[filter]?.();
  }, [filter, setSolarSitesReset, setStorageSitesReset, setWindSitesReset]);

  // TODO: add logic in below hook for different sites table type storage and wind
  const {
    data: sites,
    filterValues,
    isLoading,
    siteTypesToShow,
  } = useSitesTable({
    sortDirection:
      UpdateColAndSort[filter]?.sortDirection ||
      UpdateColAndSort[EntityType.WIND_SITE]?.sortDirection,
    sortMetric:
      UpdateColAndSort[filter]?.sortMetric || UpdateColAndSort[EntityType.WIND_SITE]?.sortMetric,
    search: currentSearch,
    filters: FilterDefsFromSiteTypeFilter[filter],
    filterSequence: FilterDefsFilterSequence[filter],
    siteType: filter,
  });

  const { onVisibilityChange: handleColumnChanges, onSelectionVisibilityChange } =
    useManageColumnVisibility({
      subKey: STORAGE_KEYS[filter] || STORAGE_KEYS[EntityType.WIND_SITE],
      featPrefs,
      savePrefs,
      setVisibleColumns:
        UpdateColAndSort[filter]?.setVisibleColumns ||
        UpdateColAndSort[EntityType.WIND_SITE]?.setVisibleColumns,
      updateColumnVisibility:
        UpdateColAndSort[filter]?.updateColumnVisibility ||
        UpdateColAndSort[EntityType.WIND_SITE]?.updateColumnVisibility,
    });

  const modifiedNavItems = useMemo(() => {
    return SitesNavItems.filter(({ capabilities }) =>
      capabilities ? isAuthorized({ capabilities }) : true,
    )?.filter((navItem) => siteTypesToShow[navItem?.label?.toLowerCase()]);
  }, [isAuthorized, siteTypesToShow]);
  // Process URL params for setting initial view type.
  useEffect(() => {
    const params = new URLSearchParams(search);
    const view = params.get('view');

    if (view) {
      setActiveState(view);
    } else {
      setActiveState(PresentationTypes.LIST);
    }
  }, [isActive, search]);

  const handleSiteRowSelect = useCallback(
    (e, row) => {
      const {
        site: { id: siteId },
      } = row;
      e.stopPropagation();
      switch (filter) {
        case MonitorDefs.WIND_SITES:
          showSiteDetails(siteId);
          break;
        case MonitorDefs.STORAGE_SITES:
          showStorageSiteDetails(siteId);
          break;
        case MonitorDefs.SOLAR_SITES:
          showSolarSiteDetails(siteId);
          break;
        case MonitorDefs.HYBRID_SITES:
          showHybridSiteDetails(siteId);
          break;
        default:
          showSiteDetails(siteId);
      }
      setCurrentRow([siteId]);
    },
    [showSiteDetails, showStorageSiteDetails, showHybridSiteDetails, showSolarSiteDetails, filter],
  );

  if (!VALID_ENDPOINTS.includes(filter)) {
    return <FourOhFour />;
  }

  if (!sites) {
    return null;
  }

  if (!ready || isLoading) {
    return (
      <Load>
        <Loader />
      </Load>
    );
  }

  return (
    <PageContainer i18nReady={ready} ref={containerRef}>
      <h1 className="pageTitle">{ready && t('sites', 'Sites')}</h1>
      <SubNavigation navItems={modifiedNavItems}>
        <ClearColumnFilter filters={FilterDefsFromSiteTypeFilter[filter]} onReset={resetFilters} />
        <ColumnSelectMenu
          translateFn={t}
          containerRef={containerRef}
          columnDef={columnDefsMap[filter]}
          columnState={UpdateColAndSort[filter]?.visibleCols}
          onMenuClose={handleColumnChanges}
          onClumnSelection={onSelectionVisibilityChange}
          isMenuBottom={isMobile}
        />
        <PresentationToggle viewList={PresentationViews} />
      </SubNavigation>
      {isActive === PresentationTypes.LIST && (
        <DetailsDismissInert>
          {filter === EntityType.WIND_SITE && (
            <SitesTable
              columns={UpdateColAndSort[filter]?.visibleCols}
              sites={sites}
              sortAction={UpdateColAndSort[filter]?.updateSortMetric}
              sortMetric={UpdateColAndSort[filter]?.sortMetric}
              sortDirection={UpdateColAndSort[filter]?.sortDirection}
              onRowSelect={handleSiteRowSelect}
              onDrop={(columnValue) => savePrefs(columnValue, STORAGE_KEYS[filter])}
              filterDefs={FilterDefsFromSiteTypeFilter[filter]}
              setFilterDefs={SetFilterDefsFromSiteTypeFilter[filter]}
              setCurrentSearch={setCurrentSearch}
              filterValues={filterValues}
              currentRow={currentRow}
              type={filter}
            />
          )}
          {filter === EntityType.SOLAR_SITE && (
            <SolarSitesTable
              columns={UpdateColAndSort[filter]?.visibleCols}
              sites={sites}
              sortAction={UpdateColAndSort[filter]?.updateSortMetric}
              sortMetric={UpdateColAndSort[filter]?.sortMetric}
              sortDirection={UpdateColAndSort[filter]?.sortDirection}
              onRowSelect={handleSiteRowSelect}
              onDrop={(columnValue) => savePrefs(columnValue, STORAGE_KEYS[filter])}
              filterDefs={FilterDefsFromSiteTypeFilter[filter]}
              setFilterDefs={SetFilterDefsFromSiteTypeFilter[filter]}
              setCurrentSearch={setCurrentSearch}
              filterValues={filterValues}
              currentRow={currentRow}
              type={filter}
            />
          )}
          {filter === EntityType.STORAGE_SITE && (
            <StorageSitesTable
              columns={UpdateColAndSort[filter]?.visibleCols}
              sites={sites}
              sortAction={UpdateColAndSort[filter]?.updateSortMetric}
              sortMetric={UpdateColAndSort[filter]?.sortMetric}
              sortDirection={UpdateColAndSort[filter]?.sortDirection}
              onRowSelect={handleSiteRowSelect}
              onDrop={(columnValue) => savePrefs(columnValue, STORAGE_KEYS[filter])}
              filterDefs={FilterDefsFromSiteTypeFilter[filter]}
              setFilterDefs={SetFilterDefsFromSiteTypeFilter[filter]}
              setCurrentSearch={setCurrentSearch}
              filterValues={filterValues}
              currentRow={currentRow}
              type={filter}
            />
          )}
        </DetailsDismissInert>
      )}
      {/* {isActive === PresentationTypes.MAP && <MapView />} */}
    </PageContainer>
  );
};

MonitorSites.propTypes = {
  location: PropTypes.shape({
    search: PropTypes.string,
  }),
};

MonitorSites.defaultProps = {
  location: null,
};

export default withRouter(MonitorSites);
